import {PredicateOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {offlineConversationContents} from '../../../srcCommon/services/offline/offlineConversationContents';
import {offlineDraftMessages} from '../../../srcCommon/services/offline/offlineDraftMessages';
import {isServerMessage} from '../../helpers/models/messageHelpers';
import {batch} from '../../helpers/reduxHelpers';
import {ApiConversationDataGroup} from '../../models/api/dataGroupModel';
import {MessageModel} from '../../models/api/messageModel';
import {ThunkAction} from '../../store/appThunk';
import {updateDataAction} from '../../store/data/dataGroupsActions';
import {deleteApiAsync} from '../http/http';
import {HttpResponseError} from '../http/httpError';
import {findMessageQueue} from './specializedTaskQueues';

export function deleteMessageTask(messageUid: string, onlyIf?: PredicateOf<MessageModel>): ThunkAction {
  return async (dispatch, getState) => {
    // Not using queue.get() because we want to be able to delete partial messages.
    const messageInitial = getState().data.messages[messageUid];
    if (!messageInitial) {
      return;
    }

    // Delete instantly from the store. Fire and forget to avoid task queue race conditions.
    offlineDraftMessages.removeAsync(messageInitial).catch((error) => {
      reportToBugsnag(error);
    });

    // Find the message to delete.
    const queue = findMessageQueue(dispatch, getState, messageUid);

    // Check whether we need to delete based on the optimistic state of the message.
    const shouldOptimisticallyDelete = !onlyIf || onlyIf(messageInitial);
    if (shouldOptimisticallyDelete) {
      queue.delete();
    }

    await queue.waitForTurnAsync();
    const messageAfterWait = queue.getResult() || messageInitial;

    // Check whether we need to delete based on the actual state of the message after obtaining a lock.
    const shouldDelete = !onlyIf || onlyIf(messageAfterWait);
    if (!shouldDelete) {
      // This will restore the app state to when we first called `findMessageQueue`, as if nothing happened.
      queue.provideResultAsync({});
      return;
    }

    // If the message was never saved, we're done.
    if (!isServerMessage(messageAfterWait)) {
      queue.reportDeleted();
      return;
    }

    const {conversationId} = messageAfterWait;
    const pendingTaskIsDone = offlineConversationContents.addPendingOfflineTask({
      type: 'deleteMessage',
      conversationId,
      messageUid,
    });

    try {
      // Perform the request.
      const result = await deleteApiAsync(
        ApiConversationDataGroup,
        `${messageAfterWait.url}?include_conversation=true`,
      );

      batch(() => {
        dispatch(updateDataAction(result));
        queue.reportDeleted();
      });
    } catch (error) {
      // If the message was never saved or has already been deleted, treat it as a successful deletion.
      if (error instanceof HttpResponseError && [403, 404].includes(error.status)) {
        // TODO: Should we re-fetch the conversation?
        queue.reportDeleted();
        return;
      }

      queue.reportError();
      throw error;
    } finally {
      pendingTaskIsDone();
    }
  };
}
